/**
 * Unified BUDOVA „Objekty k připojení“ / WF table for Excel AOA exports.
 * Same columns + check order for web (Objekty → Budova, polygon Export WF)
 * and Google Drive Tabulka_WF.xlsx.
 */

import {
  BUDOVA_PRIPOJENI_CHECK_COLUMNS,
  type BudovaPripojeniCheckColumn,
  type BudovaPripojeniExclusions,
} from './budova-pripojeni-exclusion.util';
import {
  formatBudovaPripojeniRozvadecCell,
  sortBudovaPripojeniRowsByRozvadec,
} from './budova-pripojeni-rozvadec-group.util';
import { TYP_CISLA_COLUMN_LABEL } from './typ-cisla.util';

/** Display labels for check columns — order matches {@link BUDOVA_PRIPOJENI_CHECK_COLUMNS}. */
export const BUDOVA_PRIPOJENI_CHECK_COLUMN_LABELS: Record<BudovaPripojeniCheckColumn, string> = {
  zavrtany: 'Zavrtaný',
  vr: 'VR',
  digitalniCesta: 'Digitální cesta',
  odboceno: 'Odbočeno',
  babovka: 'Bábovka',
};

/** Full export header row (UI table + polygon / timestamps). */
export const BUDOVA_PRIPOJENI_TABLE_EXPORT_HEADERS = [
  'Adresa',
  TYP_CISLA_COLUMN_LABEL,
  'Parcelní číslo',
  'Fáze',
  ...BUDOVA_PRIPOJENI_CHECK_COLUMNS.map((c) => BUDOVA_PRIPOJENI_CHECK_COLUMN_LABELS[c]),
  'Počet HP',
  'Počet fotek',
  'Délka záfuku',
  'Rozvaděč',
  'Délka přívodní MT',
  'Barva MT',
  'Stav ACQ',
  'Tel. kontakt',
  'Email',
  'Poznámky',
  'Polygon',
  'Poslední úprava',
] as const;

export type BudovaPripojeniTableExportHeader =
  (typeof BUDOVA_PRIPOJENI_TABLE_EXPORT_HEADERS)[number];

export interface BudovaPripojeniContactSources {
  /** Smluvní / KN kontakt (vyšší priorita). */
  ownerEmail?: string | null;
  ownerPhone?: string | null;
  /** Explicitní ACQ pole (pokud denormalizovaná). */
  contactPerson?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  ownerName?: string | null;
  /** ACQ report `raw_data` — klíče `contactPerson` / `contactEmail` / `contactPhone` / `ownerName`. */
  rawData?: Record<string, unknown> | null;
}

/**
 * Kontakt budovy: jméno + tel. + e-mail pro WF / Objekty k připojení / Frontu práce.
 * E-mail/telefon: smluvní vlastník → explicitní ACQ → report raw_data.
 * Jméno: kontaktní osoba → vlastník / firma.
 */
export function resolveBudovaPripojeniContact(
  sources: BudovaPripojeniContactSources | null | undefined,
): { contactName: string; contactPhone: string; contactEmail: string } {
  const raw = sources?.rawData ?? null;
  const rawText = (key: string): string =>
    raw && typeof raw[key] === 'string' ? (raw[key] as string).trim() : '';
  const contactEmail =
    (sources?.ownerEmail ?? '').trim() ||
    (sources?.contactEmail ?? '').trim() ||
    rawText('contactEmail');
  const contactPhone =
    (sources?.ownerPhone ?? '').trim() ||
    (sources?.contactPhone ?? '').trim() ||
    rawText('contactPhone');
  const contactName =
    (sources?.contactPerson ?? '').trim() ||
    rawText('contactPerson') ||
    (sources?.ownerName ?? '').trim() ||
    rawText('ownerName');
  return { contactName, contactPhone, contactEmail };
}

export interface BudovaPripojeniTableExportRow {
  id: string;
  name?: string | null;
  /** Ulice / obec (bez č.p. — to patří do {@link cp}). */
  address: string;
  /** Číslo popisné (+ orientační), např. `23/5`. */
  cp: string;
  parcelNumber: string;
  phaseLabel: string;
  milestones: Record<BudovaPripojeniCheckColumn, boolean>;
  exclusions: BudovaPripojeniExclusions;
  hpLabel: string;
  photoCount: number;
  zafukLengthDisplay: string;
  rozvadecLabel: string;
  /** Linked rozvaděč object id — grouping key for WF row order. */
  rozvadecObjectId?: string | null;
  privodniMtLengthDisplay: string;
  /** České názvy napojených MT (prázdné → —). */
  mtBarvaLabel: string;
  statusAcq: string;
  /** Telefon z ACQ reportu / smlouvy. */
  contactPhone: string;
  /** E-mail z ACQ reportu / smlouvy. */
  contactEmail: string;
  /** Agregované poznámky budovy + dokop + spojkoviště. */
  notesDisplay: string;
  polygonLabel: string;
  updatedAtLabel: string;
  /** Workflow status code for optional Fáze column fill. */
  statusCode?: string | null;
}

export interface BudovaPripojeniTableExportAoA {
  aoa: (string | number)[][];
  statusCodes: Array<string | null>;
  /** 1-based Excel column indexes for check columns (Zavrtaný … Bábovka). */
  checkColumnIndexes: number[];
  /**
   * Per data row, per check column (same order as {@link BUDOVA_PRIPOJENI_CHECK_COLUMNS}):
   * true = excluded (red fill, empty value).
   */
  exclusionFlags: boolean[][];
}

/** Done → „Ano“; excluded / not filled → empty. */
export function formatBudovaPripojeniCheckExportCell(
  done: boolean,
  excluded: boolean,
): string {
  if (excluded) return '';
  return done ? 'Ano' : '';
}

/** 1-based column index of the first check column in the export header. */
export function budovaPripojeniCheckColumnsStartIndex(): number {
  return BUDOVA_PRIPOJENI_TABLE_EXPORT_HEADERS.indexOf(
    BUDOVA_PRIPOJENI_CHECK_COLUMN_LABELS.zavrtany,
  ) + 1;
}

export function buildBudovaPripojeniTableExportAoA(
  rows: readonly BudovaPripojeniTableExportRow[],
): BudovaPripojeniTableExportAoA {
  const headers = [...BUDOVA_PRIPOJENI_TABLE_EXPORT_HEADERS];
  const checkStart = budovaPripojeniCheckColumnsStartIndex();
  const checkColumnIndexes = BUDOVA_PRIPOJENI_CHECK_COLUMNS.map((_, i) => checkStart + i);
  const ordered = sortBudovaPripojeniRowsByRozvadec(rows);

  const aoa: (string | number)[][] = [headers];
  const statusCodes: Array<string | null> = [];
  const exclusionFlags: boolean[][] = [];

  for (const row of ordered) {
    statusCodes.push(row.statusCode ?? null);
    exclusionFlags.push(BUDOVA_PRIPOJENI_CHECK_COLUMNS.map((c) => row.exclusions[c] === true));
    aoa.push([
      row.address,
      row.cp,
      row.parcelNumber,
      row.phaseLabel,
      ...BUDOVA_PRIPOJENI_CHECK_COLUMNS.map((c) =>
        formatBudovaPripojeniCheckExportCell(row.milestones[c] === true, row.exclusions[c] === true),
      ),
      row.hpLabel,
      row.photoCount,
      row.zafukLengthDisplay,
      formatBudovaPripojeniRozvadecCell(row.rozvadecLabel),
      row.privodniMtLengthDisplay,
      row.mtBarvaLabel,
      row.statusAcq,
      row.contactPhone,
      row.contactEmail,
      row.notesDisplay,
      row.polygonLabel,
      row.updatedAtLabel,
    ]);
  }

  return { aoa, statusCodes, checkColumnIndexes, exclusionFlags };
}
